import type { GameId } from '@ofc/shared';

/**
 * How each game's stats read on a phone.
 *
 * Every game's server already keeps a record per player and for the whole site,
 * each in its own shape. These turn any of them into the same two things: a few
 * headline figures, and a ranked list or two.
 */

export interface Figure {
  label: string;
  value: string;
}

export interface Ranked {
  title: string;
  rows: { label: string; value: string }[];
}

export interface StatsView {
  figures: Figure[];
  lists: Ranked[];
}

/* eslint-disable @typescript-eslint/no-explicit-any -- each game's JSON, read defensively */
type Json = any;

const num = (n: unknown): string => (typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('en-GB') : '—');
/** A share from 0 to 1. */
const share = (n: unknown): string => (typeof n === 'number' ? `${Math.round(n * 100)}%` : '—');
/** Already a percentage. */
const pct = (n: unknown): string => (typeof n === 'number' ? `${Math.round(n)}%` : '—');
const dist = (n: unknown): string => (typeof n === 'number' ? n.toFixed(1) : '—');
const secs = (ms: unknown): string => (typeof ms === 'number' ? `${(ms / 1000).toFixed(1)}s` : '—');
const fig = (label: string, value: string): Figure => ({ label, value });
const list = (title: string, rows: { label: string; value: string }[]): Ranked[] =>
  rows.length > 0 ? [{ title, rows: rows.slice(0, 5) }] : [];
const arr = (x: unknown): Json[] => (Array.isArray(x) ? x : []);

/** A solo or duel game's record: the same shape for all ten. */
const ARCADE_BEST: Partial<Record<GameId, string>> = {
  higherlower: 'Longest run',
  castreveal: 'Best score',
  posterzoom: 'Best score',
  sixdegrees: 'Best score',
  connect4: 'Best',
  noughts: 'Best',
  battleships: 'Most hits',
  dots: 'Best',
  wordduel: 'Best score',
  anagrams: 'Best score',
  poker: 'Biggest pot',
  liarsdice: 'Dice left in a win',
  whosaid: 'Best score',
  wouldyourather: 'Best score',
  cheat: 'Best',
  rankem: 'Best score',
  spotlight: 'Best score',
  blindranking: 'Best score',
  onestar: 'Best score',
  wagerquiz: 'Best finish',
  fakinit: 'Best score',
  undercover: 'Best score',
  mole: 'Best score',
  boxart: 'Best score',
};

function arcadePlayer(game: GameId): { url(username: string): string; view(d: Json): StatsView } {
  return {
    url: (u) => `/api/${game}/stats/player/${encodeURIComponent(u)}`,
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Against others', num(d.duels)),
        fig('Wins', num(d.wins)),
        fig(ARCADE_BEST[game] ?? 'Best', num(d.best)),
        ...Object.entries((d.counts ?? {}) as Record<string, number>)
          .filter(([k]) => k !== 'fastestMs' && k !== 'fewest' && k !== 'par')
          .slice(0, 3)
          .map(([k, value]) => fig(k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()), num(value))),
      ],
      lists: [],
    }),
  };
}

function arcadeGlobal(game: GameId): { url: string; view(d: Json): StatsView } {
  return {
    url: `/api/${game}/stats/global`,
    view: (d) => ({
      figures: [fig('Games', num(d.games))],
      lists: [
        ...list(ARCADE_BEST[game] ?? 'Best', arr(d.best).map((p) => ({ label: p.username, value: num(p.best) }))),
        ...list('Most wins', arr(d.mostWins).map((p) => ({ label: p.username, value: num(p.wins) }))),
      ],
    }),
  };
}

const ARCADE_IDS = ['higherlower', 'castreveal', 'posterzoom', 'sixdegrees', 'connect4', 'noughts', 'battleships', 'dots', 'wordduel', 'anagrams', 'poker', 'liarsdice', 'whosaid', 'wouldyourather', 'cheat', 'rankem', 'spotlight', 'blindranking', 'onestar', 'wagerquiz', 'fakinit', 'undercover', 'mole', 'boxart'] as const;

/** Your own record in one game. */
export const PLAYER_STATS: Record<GameId, { url(username: string): string; view(d: Json): StatsView }> = {
  movie: {
    url: (u) => `/api/stats/${encodeURIComponent(u)}`,
    view: (d) => ({
      figures: [
        fig('Games', num(d.gamesPlayed)),
        fig('Survived', num(d.gamesPlayed - d.gamesLost)),
        fig('Films named', num(d.moviesPlayed)),
        fig('Longest chain', num(d.longestChain)),
        fig('Quickest answer', secs(d.fastestAnswerMs)),
      ],
      lists: [],
    }),
  },
  quiz: {
    url: (u) => `/api/quiz/stats/player/${encodeURIComponent(u)}`,
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Accuracy', share(d.accuracy)),
        fig('Best streak', num(d.bestStreak)),
        fig('High score', num(d.highScore)),
        fig('Average answer', secs(d.averageResponseMs)),
      ],
      lists: d.bestPack ? list('Best category', [{ label: d.bestPack.label, value: share(d.bestPack.accuracy) }]) : [],
    }),
  },
  quiplash: {
    url: (u) => `/api/quiplash/stats/player/${encodeURIComponent(u)}`,
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Wins', num(d.victories)),
        fig('Prompts won', num(d.wins)),
        fig('Votes', num(d.votesReceived)),
        fig('Super quips', num(d.superQuips)),
        fig('High score', num(d.highScore)),
      ],
      lists: [],
    }),
  },
  fibbage: {
    url: (u) => `/api/fibbage/stats/player/${encodeURIComponent(u)}`,
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Wins', num(d.victories)),
        fig('Fooled', num(d.playersFooled)),
        fig('Got fooled', num(d.timesFooled)),
        fig('Found the truth', share(d.accuracy)),
        fig('High score', num(d.highScore)),
      ],
      lists: d.bestLie ? list('Best lie', [{ label: `“${d.bestLie.text}”`, value: `${d.bestLie.fooled} fooled` }]) : [],
    }),
  },
  enough: {
    url: (u) => `/api/enough/stats/player/${encodeURIComponent(u)}`,
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Wins', num(d.victories)),
        fig('Fooled', num(d.playersFooled)),
        fig('Read right', num(d.timesRecognised)),
        fig('Guessed right', share(d.accuracy)),
        fig('High score', num(d.highScore)),
      ],
      lists: d.mostBelievableLie
        ? list('Most believable', [{ label: `“${d.mostBelievableLie.text}”`, value: `${d.mostBelievableLie.fooled} fooled` }])
        : [],
    }),
  },
  wavelengths: {
    url: (u) => `/api/wavelengths/stats/player/${encodeURIComponent(u)}`,
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Wins', num(d.victories)),
        fig('Clues given', num(d.cluesGiven)),
        fig('Average miss', dist(d.averageDistance)),
        fig('Closest', dist(d.bestDistance)),
        fig('High score', num(d.highScore)),
      ],
      lists: [],
    }),
  },
  obscurity: {
    url: (u) => `/api/obscurity/stats/player/${encodeURIComponent(u)}`,
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Wins', num(d.victories)),
        fig('Answers', num(d.answersGiven)),
        fig('Legendary', num(d.legendary)),
        fig('One in a million', num(d.oneInAMillion)),
        fig('High score', num(d.highScore)),
      ],
      lists: d.bestAnswer ? list('Most obscure', [{ label: d.bestAnswer, value: d.bestTier ?? '' }]) : [],
    }),
  },
  superlatives: {
    url: (u) => `/api/superlatives/stats/player/${encodeURIComponent(u)}`,
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Votes for you', num(d.votesReceived)),
        fig('With the room', num(d.majorityWins)),
        fig('Alignment', pct(d.alignment)),
        fig('Best round', num(d.bestRound)),
      ],
      lists: [],
    }),
  },
  chameleon: {
    url: (u) => `/api/chameleon/stats/player/${encodeURIComponent(u)}`,
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Chameleon', num(d.timesChameleon)),
        fig('Escapes', num(d.escapes)),
        fig('Spotted', num(d.spotted)),
      ],
      lists: [],
    }),
  },
  hottakes: {
    url: (u) => `/api/hottakes/stats/player/${encodeURIComponent(u)}`,
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Takes', num(d.ratingsGiven)),
        fig('Spot on', num(d.spotOn)),
        fig('Hottest take', num(d.hottest)),
        fig('Off the room by', dist(d.offBy)),
      ],
      lists: [],
    }),
  },
  ...(Object.fromEntries(ARCADE_IDS.map((g) => [g, arcadePlayer(g)])) as Record<(typeof ARCADE_IDS)[number], ReturnType<typeof arcadePlayer>>),
};

/** The whole site's record in one game. The Movie Game has its own, richer view. */
export const GLOBAL_STATS: Record<Exclude<GameId, 'movie'>, { url: string; view(d: Json): StatsView }> = {
  quiz: {
    url: '/api/quiz/stats/global',
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Questions answered', num(d.questionsAnswered)),
        fig('Most popular', d.mostPopular?.label ?? '—'),
        fig('Hardest', d.hardest?.label ?? '—'),
      ],
      lists: list(
        'Categories by accuracy',
        arr(d.packs).map((p) => ({ label: p.label, value: share(p.accuracy) })),
      ),
    }),
  },
  quiplash: {
    url: '/api/quiplash/stats/global',
    view: (d) => ({
      figures: [fig('Games', num(d.games)), fig('Super quips', num(d.superQuips)), fig('Average margin', dist(d.averageMargin))],
      lists: list(
        'Most super quips',
        arr(d.mostSuperQuips).map((p) => ({ label: p.text, value: num(p.superQuips) })),
      ),
    }),
  },
  fibbage: {
    url: '/api/fibbage/stats/global',
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Super lies', num(d.superLies)),
        fig('Best liar', d.mostEffectiveLiar ? `${d.mostEffectiveLiar.name} (${d.mostEffectiveLiar.playersFooled})` : '—'),
      ],
      lists: list(
        'Hardest questions',
        arr(d.hardest).map((q) => ({ label: q.text, value: `${share(q.correctRate)} right` })),
      ),
    }),
  },
  enough: {
    url: '/api/enough/stats/global',
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Most believable', d.mostBelievableLiar ? `${d.mostBelievableLiar.name} (${d.mostBelievableLiar.playersFooled})` : '—'),
      ],
      lists: list(
        'Easiest to read',
        arr(d.mostGuessed).map((p) => ({ label: p.text, value: share(p.recognisedRate) })),
      ),
    }),
  },
  wavelengths: {
    url: '/api/wavelengths/stats/global',
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Best clue giver', d.bestClueGiver?.name ?? '—'),
        fig('Sharpest guesser', d.sharpestGuesser?.name ?? '—'),
      ],
      lists: list(
        'Hardest dials',
        arr(d.hardest).map((s) => ({ label: `${s.left} ↔ ${s.right}`, value: dist(s.averageDistance) })),
      ),
    }),
  },
  obscurity: {
    url: '/api/obscurity/stats/global',
    view: (d) => ({
      figures: [
        fig('Games', num(d.games)),
        fig('Most obscure', d.mostObscure ? `${d.mostObscure.answer} (${d.mostObscure.name})` : '—'),
      ],
      lists: list(
        'Hardest topics',
        arr(d.hardest).map((t) => ({ label: t.name, value: dist(t.averageScore) })),
      ),
    }),
  },
  superlatives: {
    url: '/api/superlatives/stats/global',
    view: (d) => ({
      figures: [fig('Games', num(d.games)), fig('Rounds', num(d.rounds))],
      lists: [
        ...list('Most voted for', arr(d.mostVoted).map((p) => ({ label: p.username, value: num(p.votes) }))),
        ...list('Most played prompts', arr(d.popularPrompts).map((p) => ({ label: p.text, value: num(p.plays) }))),
      ],
    }),
  },
  chameleon: {
    url: '/api/chameleon/stats/global',
    view: (d) => ({
      figures: [fig('Games', num(d.games)), fig('Rounds', num(d.rounds)), fig('Escape rate', pct(d.escapeRate))],
      lists: [
        ...list('Slipperiest', arr(d.bestChameleons).map((p) => ({ label: p.username, value: `${p.escapes} escapes` }))),
        ...list('Sharpest eyes', arr(d.sharpestEyes).map((p) => ({ label: p.username, value: `${p.spotted} spotted` }))),
      ],
    }),
  },
  hottakes: {
    url: '/api/hottakes/stats/global',
    view: (d) => ({
      figures: [fig('Games', num(d.games)), fig('Rounds', num(d.rounds))],
      lists: [
        ...list('Most divisive', arr(d.divisive).map((s) => ({ label: s.text, value: `±${dist(s.spread)}` }))),
        ...list('Everyone agrees', arr(d.consensus).map((s) => ({ label: s.text, value: dist(s.average) }))),
      ],
    }),
  },
  ...(Object.fromEntries(ARCADE_IDS.map((g) => [g, arcadeGlobal(g)])) as Record<(typeof ARCADE_IDS)[number], ReturnType<typeof arcadeGlobal>>),
};
